/* ============================================================================================
 * Chargeur de traceurs (pixel Meta, Google, PostHog, TikTok, Pinterest, Clarity, Bing...).
 *
 * - La liste des traceurs vient du serveur (service getTrackerConfig) : uniquement des
 *   IDENTIFIANTS, validés par expression régulière côté serveur ET revérifiés ici. Aucun
 *   script libre n'est jamais exécuté : chaque traceur a son code de chargement écrit ci-dessous.
 * - RGPD / CNIL : rien n'est chargé avant que le visiteur ait accepté (bandeau de
 *   TrackerConsent.js). Google reçoit en plus le signal Consent Mode v2.
 * - Rien sur localhost (sauf test volontaire : localStorage.jm_trackers_debug = '1').
 * - Rien sur les pages privées (éditeur de mini-site dont l'adresse contient un jeton secret,
 *   pages des invités) ni sur les écrans d'administration.
 * - relayEvent() traduit les événements e-commerce déjà émis par trackEvent() vers chaque
 *   traceur actif : les composants n'ont rien à appeler de plus.
 * ============================================================================================ */
import { apiUrl } from './apiBase';
import { stripLang } from '../i18n/paths';

const CONSENT_KEY = 'jm_consent';
const CONFIG_CACHE_KEY = 'jm_tracker_config';
const CONSENT_MAX_AGE_MS = 182 * 24 * 3600 * 1000; // la CNIL recommande de redemander au bout de 6 mois
const CONFIG_CACHE_MS = 10 * 60 * 1000;

// Mêmes formats que le serveur (api-2026-marketing.php, jmTrackerCatalog) : double vérification.
const ID_FORMATS = {
  gtm: /^GTM-[A-Z0-9]{4,12}$/,
  ga4: /^G-[A-Z0-9]{6,16}$/,
  google_ads: /^AW-[0-9]{6,15}$/,
  meta_pixel: /^[0-9]{10,20}$/,
  posthog: /^phc_[A-Za-z0-9]{20,80}$/,
  tiktok: /^[A-Z0-9]{16,24}$/,
  pinterest: /^[0-9]{10,16}$/,
  clarity: /^[a-z0-9]{8,14}$/,
  bing_uet: /^[0-9]{6,12}$/,
};
const POSTHOG_HOSTS = ['https://eu.i.posthog.com', 'https://us.i.posthog.com'];
const ADS_LABEL_FORMAT = /^[A-Za-z0-9_-]{6,40}$/;

// Nom montré au visiteur dans « En savoir plus » du bandeau (noms de marque : jamais traduits).
// La finalité de chaque outil est un texte traduisible : src/i18n/locales/<langue>/consent.json,
// clé consent.trackers.<clé du traceur>.
export const TRACKER_PUBLIC_INFO = {
  gtm: { name: 'Google Tag Manager' },
  ga4: { name: 'Google Analytics' },
  google_ads: { name: 'Google Ads' },
  meta_pixel: { name: 'Meta (Facebook / Instagram)' },
  posthog: { name: 'PostHog' },
  tiktok: { name: 'TikTok' },
  pinterest: { name: 'Pinterest' },
  clarity: { name: 'Microsoft Clarity' },
  bing_uet: { name: 'Microsoft Advertising (Bing)' },
};

// Les trois traceurs historiquement en dur dans index.html : utilisés seulement si le serveur ne
// répond pas ET qu'aucune configuration n'a jamais été reçue (un déploiement ne coupe pas la mesure).
const FALLBACK_CONFIG = [
  { key: 'ga4', id: 'G-ZJ0FRTJ7Z3', options: {}, enabled: true },
  { key: 'gtm', id: 'GTM-KP26X763', options: {}, enabled: true },
  { key: 'posthog', id: 'phc_XXZkp34kYBC7BQkUsrHkKfontCZrV2VhzI6BMzRza6X', options: { host: 'https://eu.i.posthog.com' }, enabled: true },
];

// Pages où AUCUN traceur tiers ne doit tourner : adresse contenant un jeton secret, pages des invités.
const PRIVATE_PREFIXES = ['/personnaliser-mini-site', '/apercu-mini-site', '/i/', '/carte-a-gratter/', '/carte-surprise/', '/carte-old/'];
// Écrans d'administration : pas de traceur, pas de bandeau (ils fausseraient les statistiques).
const ADMIN_PREFIXES = ['/gestion-commandes', '/gestion-catalogue', '/bibliotheque-visuels', '/gestion-promos', '/list-cards',
  '/creer-card', '/edit-card', '/analytics', '/trackers', '/tunnel-acquisition', '/usage-produits'];

// Le préfixe de langue (/en/…, /de/…) est retiré avant la comparaison : une page privée l'est dans toutes les langues.
export const isPrivatePath = (pathname) => { const p = stripLang(pathname); return PRIVATE_PREFIXES.some((prefix) => p.startsWith(prefix)); };
export const isNoTrackerPath = (pathname) => { const p = stripLang(pathname); return isPrivatePath(p) || ADMIN_PREFIXES.some((prefix) => p.startsWith(prefix)); };

const safeStorage = {
  get(key) { try { return window.localStorage.getItem(key); } catch (e) { return null; } },
  set(key, value) { try { window.localStorage.setItem(key, value); } catch (e) { /* navigation privée */ } },
  remove(key) { try { window.localStorage.removeItem(key); } catch (e) { /* idem */ } },
};

export const isBlockedOnThisHost = () => {
  const local = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  return local && safeStorage.get('jm_trackers_debug') !== '1';
};

/* ---------------------------------------------------------------- consentement ---- */

export const getConsent = () => {
  try {
    const saved = JSON.parse(safeStorage.get(CONSENT_KEY) || 'null');
    if (!saved || !saved.choice || Date.now() - saved.at > CONSENT_MAX_AGE_MS) return null;
    return saved.choice === 'accepted' ? 'accepted' : 'refused';
  } catch (e) {
    return null;
  }
};

export const saveConsent = (choice) => {
  safeStorage.set(CONSENT_KEY, JSON.stringify({ choice, at: Date.now() }));
};

// Retrait du consentement : on efface au mieux les cookies posés par les traceurs (même domaine).
export const purgeTrackerCookies = () => {
  const patterns = [/^_ga/, /^_gid$/, /^_gcl_/, /^_gat/, /^_fbp$/, /^_fbc$/, /^ph_/, /^_ttp$/, /^_tt_/, /^_pin_unauth$/, /^_pinterest_/, /^_clck$/, /^_clsk$/, /^_uet/];
  const host = window.location.hostname;
  const domains = ['', host, `.${host}`, `.${host.split('.').slice(-2).join('.')}`];
  document.cookie.split(';').forEach((cookie) => {
    const name = cookie.split('=')[0].trim();
    if (!patterns.some((re) => re.test(name))) return;
    domains.forEach((d) => {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${d ? `; domain=${d}` : ''}`;
    });
  });
  try {
    Object.keys(window.localStorage).filter((k) => k.startsWith('ph_')).forEach((k) => window.localStorage.removeItem(k));
  } catch (e) { /* rien */ }
};

/* --------------------------------------------------------------- configuration ---- */

const sanitizeConfig = (list) => (Array.isArray(list) ? list : [])
  .filter((t) => t && t.enabled && ID_FORMATS[t.key] && typeof t.id === 'string' && ID_FORMATS[t.key].test(t.id))
  .map((t) => ({ key: t.key, id: t.id, options: t.options && typeof t.options === 'object' ? t.options : {} }));

let configPromise = null;

/** Traceurs actifs (tableau, éventuellement vide). Ne rejette jamais. */
export const fetchActiveTrackers = () => {
  if (isBlockedOnThisHost()) return Promise.resolve([]);
  if (configPromise) return configPromise;
  let cached = null;
  try { cached = JSON.parse(safeStorage.get(CONFIG_CACHE_KEY) || 'null'); } catch (e) { cached = null; }
  if (cached && Date.now() - cached.at < CONFIG_CACHE_MS) {
    configPromise = Promise.resolve(sanitizeConfig(cached.trackers));
    return configPromise;
  }
  configPromise = fetch(apiUrl('getTrackerConfig'))
    .then((res) => res.json())
    .then((json) => {
      if (!json || !json.success || !Array.isArray(json.trackers)) throw new Error('réponse inattendue');
      safeStorage.set(CONFIG_CACHE_KEY, JSON.stringify({ at: Date.now(), trackers: json.trackers }));
      return sanitizeConfig(json.trackers);
    })
    .catch(() => sanitizeConfig(cached ? cached.trackers : FALLBACK_CONFIG));
  return configPromise;
};

/** À appeler après un enregistrement dans la page admin : la prochaine lecture repart du serveur. */
export const clearTrackerConfigCache = () => {
  safeStorage.remove(CONFIG_CACHE_KEY);
  configPromise = null;
};

/* ------------------------------------------------------ chargement des scripts ---- */

// Choix du propriétaire (2026-09-19) : pas de trou dans la mesure. Deux temps :
//  1. MESURE, dès l'arrivée, sans cookie : GA4 et GTM avec le Consent Mode de Google laissé sur
//     « refusé » (signaux anonymes, aucun cookie), PostHog avec une mémoire limitée à l'onglet.
//  2. PUBLICITÉ, seulement après « Accepter » : pixels Meta, TikTok, Pinterest, Google Ads, Bing,
//     Clarity — et passage de GA4/GTM/PostHog en mode complet (cookies).
// Le suivi maison (tracking_visits / tracking_events) ne dépend de rien de tout cela.
const MEASURE_KEYS = ['ga4', 'gtm', 'posthog'];

const active = {};          // clé du traceur → configuration, une fois chargé
let trackersLoaded = false; // temps 1 (mesure) fait
let adTrackersLoaded = false; // temps 2 (publicité, après accord) fait
let pendingEvents = [];     // événements émis avant le chargement (ex. product_view à l'arrivée)

export const areTrackersLoaded = () => trackersLoaded;
export const areAdTrackersLoaded = () => adTrackersLoaded;

const loadScript = (src, onload) => {
  const s = document.createElement('script');
  s.async = true;
  s.src = src;
  if (onload) s.onload = onload;
  document.head.appendChild(s);
};

function gtag() { window.dataLayer.push(arguments); } // eslint-disable-line prefer-rest-params

const CONSENT_DENIED = { ad_storage: 'denied', ad_user_data: 'denied', ad_personalization: 'denied', analytics_storage: 'denied' };
const CONSENT_GRANTED = { ad_storage: 'granted', ad_user_data: 'granted', ad_personalization: 'granted', analytics_storage: 'granted' };

const loaders = {
  // Google, temps 1 : Consent Mode v2 sur « refusé » AVANT tout script, puis GA4 et GTM.
  google(trackers) {
    const ga4 = trackers.find((t) => t.key === 'ga4');
    const gtm = trackers.find((t) => t.key === 'gtm');
    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || gtag;
    window.gtag('consent', 'default', CONSENT_DENIED);
    if (ga4) {
      window.gtag('js', new Date());
      window.gtag('config', ga4.id);
      loadScript(`https://www.googletagmanager.com/gtag/js?id=${ga4.id}`);
    }
    if (gtm) {
      window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
      loadScript(`https://www.googletagmanager.com/gtm.js?id=${gtm.id}`);
    }
  },
  // Google, temps 2 (après accord) : levée du refus, et Google Ads.
  googleConsented(trackers) {
    const ga4 = trackers.find((t) => t.key === 'ga4');
    const ads = trackers.find((t) => t.key === 'google_ads');
    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || gtag;
    window.gtag('consent', 'update', CONSENT_GRANTED);
    if (ads) {
      if (!ga4) {
        window.gtag('js', new Date());
        loadScript(`https://www.googletagmanager.com/gtag/js?id=${ads.id}`);
      }
      window.gtag('config', ads.id);
    }
  },
  meta_pixel(t) {
    if (!window.fbq) {
      const n = function fbqStub() { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments); }; // eslint-disable-line
      window.fbq = n;
      if (!window._fbq) window._fbq = n;
      n.push = n; n.loaded = true; n.version = '2.0'; n.queue = [];
      loadScript('https://connect.facebook.net/en_US/fbevents.js');
    }
    window.fbq('init', t.id);
    window.fbq('track', 'PageView');
  },
  posthog(t) {
    const host = POSTHOG_HOSTS.includes(t.options.host) ? t.options.host : POSTHOG_HOSTS[0];
    /* eslint-disable */
    // Extrait officiel PostHog (identique à celui qui était dans index.html)
    !function(t,e){var o,n,p,r;e.__SV||(window.posthog=e,e._i=[],e.init=function(i,s,a){function g(t,e){var o=e.split(".");2==o.length&&(t=t[o[0]],e=o[1]),t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}}(p=t.createElement("script")).type="text/javascript",p.crossOrigin="anonymous",p.async=!0,p.src=s.api_host.replace(".i.posthog.com","-assets.i.posthog.com")+"/static/array.js",(r=t.getElementsByTagName("script")[0]).parentNode.insertBefore(p,r);var u=e;for(void 0!==a?u=e[a]=[]:a="posthog",u.people=u.people||[],u.toString=function(t){var e="posthog";return"posthog"!==a&&(e+="."+a),t||(e+=" (stub)"),e},u.people.toString=function(){return u.toString(1)+".people (stub)"},o="init capture register register_once register_for_session unregister unregister_for_session getFeatureFlag getFeatureFlagPayload isFeatureEnabled reloadFeatureFlags updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures on onFeatureFlags onSessionId getSurveys getActiveMatchingSurveys renderSurvey canRenderSurvey identify setPersonProperties group resetGroups setPersonPropertiesForFlags resetPersonPropertiesForFlags setGroupPropertiesForFlags resetGroupPropertiesForFlags reset get_distinct_id getGroups get_session_id get_session_replay_url alias set_config startSessionRecording stopSessionRecording sessionRecordingStarted captureException loadToolbar get_property getSessionProperty createPersonProfile opt_in_capturing opt_out_capturing has_opted_in_capturing has_opted_out_capturing clear_opt_in_out_capturing debug getPageViewId captureTraceFeedback captureTraceMetric".split(" "),n=0;n<o.length;n++)g(u,o[n]);e._i.push([i,s,a])},e.__SV=1)}(document,window.posthog||[]);
    /* eslint-enable */
    // Avant accord : aucune trace laissée dans le navigateur (mémoire de l'onglet uniquement).
    window.posthog.init(t.id, {
      api_host: host, person_profiles: 'identified_only', persistence: t.consented ? 'localStorage+cookie' : 'memory',
    });
  },
  tiktok(t) {
    if (!window.ttq) {
      const ttq = []; window.TiktokAnalyticsObject = 'ttq'; window.ttq = ttq;
      ttq.methods = ['page', 'track', 'identify', 'instances', 'debug', 'on', 'off', 'once', 'ready', 'alias', 'group', 'enableCookie', 'disableCookie', 'holdConsent', 'revokeConsent', 'grantConsent'];
      ttq.setAndDefer = (obj, method) => { obj[method] = function deferred() { obj.push([method].concat(Array.prototype.slice.call(arguments, 0))); }; }; // eslint-disable-line
      ttq.methods.forEach((m) => ttq.setAndDefer(ttq, m));
      ttq.load = (pixelId) => {
        const url = 'https://analytics.tiktok.com/i18n/pixel/events.js';
        ttq._i = ttq._i || {}; ttq._i[pixelId] = []; ttq._i[pixelId]._u = url;
        ttq._t = ttq._t || {}; ttq._t[pixelId] = +new Date();
        ttq._o = ttq._o || {}; ttq._o[pixelId] = {};
        loadScript(`${url}?sdkid=${pixelId}&lib=ttq`);
      };
    }
    window.ttq.load(t.id);
    window.ttq.page();
  },
  pinterest(t) {
    if (!window.pintrk) {
      const p = function pintrkStub() { p.queue.push(Array.prototype.slice.call(arguments)); }; // eslint-disable-line
      p.queue = []; p.version = '3.0';
      window.pintrk = p;
      loadScript('https://s.pinimg.com/ct/core.js');
    }
    window.pintrk('load', t.id);
    window.pintrk('page');
  },
  clarity(t) {
    window.clarity = window.clarity || function clarityStub() { (window.clarity.q = window.clarity.q || []).push(arguments); }; // eslint-disable-line
    loadScript(`https://www.clarity.ms/tag/${t.id}`);
    window.clarity('consent');
  },
  bing_uet(t) {
    window.uetq = window.uetq || [];
    window.uetq.push('consent', 'default', { ad_storage: 'granted' });
    loadScript('https://bat.bing.com/bat.js', () => {
      const options = { ti: t.id, enableAutoSpaTracking: true };
      options.q = window.uetq;
      window.uetq = new window.UET(options);
      window.uetq.push('pageLoad');
    });
  },
};

/**
 * Charge les traceurs actifs, hors pages privées/admin. `consented` = le visiteur a cliqué
 * « Accepter ». Peut être rappelée quand le consentement arrive : chaque temps ne s'exécute qu'une fois.
 */
export const loadTrackers = (trackers, consented = false) => {
  if (isBlockedOnThisHost() || !trackers.length) return;
  const measure = trackers.filter((t) => MEASURE_KEYS.includes(t.key));
  const ads = trackers.filter((t) => !MEASURE_KEYS.includes(t.key));

  if (!trackersLoaded) {
    trackersLoaded = true;
    measure.forEach((t) => { active[t.key] = t; });
    if (measure.some((t) => t.key !== 'posthog')) {
      try { loaders.google(measure); } catch (e) { console.error('Traceurs Google :', e); }
    }
    const posthog = measure.find((t) => t.key === 'posthog');
    if (posthog) {
      try { loaders.posthog({ ...posthog, consented }); } catch (e) { console.error('Traceur posthog :', e); }
    }
  }

  if (consented && !adTrackersLoaded) {
    adTrackersLoaded = true;
    ads.forEach((t) => { active[t.key] = t; });
    try { loaders.googleConsented(trackers); } catch (e) { console.error('Traceurs Google :', e); }
    try {
      if (active.posthog && window.posthog?.set_config) window.posthog.set_config({ persistence: 'localStorage+cookie' });
    } catch (e) { /* PostHog pas encore prêt : il gardera la mémoire de l'onglet */ }
    ads.forEach((t) => {
      if (t.key === 'google_ads' || !loaders[t.key]) return;
      try { loaders[t.key](t); } catch (e) { console.error(`Traceur ${t.key} :`, e); }
    });
  }

  const queued = pendingEvents;
  pendingEvents = [];
  queued.forEach(([type, data]) => relayEvent(type, data));
};

/** Changement de page dans l'application (les traceurs ne voient que le 1er chargement sinon). */
export const relayPageView = () => {
  if (!trackersLoaded) return;
  try {
    // GA4, GTM, PostHog, Clarity et Bing suivent eux-mêmes les changements d'adresse.
    if (active.meta_pixel) window.fbq('track', 'PageView');
    if (active.tiktok) window.ttq.page();
    if (active.pinterest) window.pintrk('page');
  } catch (e) { /* un traceur en panne ne doit jamais gêner le site */ }
};

/**
 * Relais des événements e-commerce émis par trackEvent() (TrackingProvider.js) vers chaque
 * traceur actif, au format que chacun attend. eventData = { label, value, product_id, order_id }.
 * eventData.gtmDone = true : l'appelant a déjà fait lui-même le dataLayer.push pour GTM.
 */
export function relayEvent(eventType, eventData = {}) {
  try {
    const known = ['product_view', 'add_to_cart', 'checkout_start', 'purchase'];
    if (!known.includes(eventType) || isBlockedOnThisHost() || isNoTrackerPath(window.location.pathname)) return;
    if (!trackersLoaded) {
      if (pendingEvents.length < 20) pendingEvents.push([eventType, eventData]);
      return;
    }
    const value = Number.isFinite(parseFloat(eventData.value)) ? parseFloat(eventData.value) : undefined;
    const itemId = eventData.product_id ? String(eventData.product_id) : undefined;
    const itemName = eventData.label || undefined;
    const orderId = eventData.order_id ? String(eventData.order_id) : undefined;
    const isProductEvent = eventType === 'product_view' || eventType === 'add_to_cart';

    // --- Google (noms d'événements e-commerce standard de GA4)
    const gaName = { product_view: 'view_item', add_to_cart: 'add_to_cart', checkout_start: 'begin_checkout', purchase: 'purchase' }[eventType];
    const ecommerce = { currency: 'EUR', value };
    if (isProductEvent) ecommerce.items = [{ item_id: itemId || itemName, item_name: itemName, price: value, quantity: 1 }];
    if (eventType === 'purchase') ecommerce.transaction_id = orderId;
    if (active.ga4) window.gtag('event', gaName, { ...ecommerce, send_to: active.ga4.id });
    if (active.gtm && !eventData.gtmDone) {
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({ event: gaName, ecommerce });
    }
    if (active.google_ads && eventType === 'purchase' && ADS_LABEL_FORMAT.test(active.google_ads.options.purchase_label || '')) {
      window.gtag('event', 'conversion', { send_to: `${active.google_ads.id}/${active.google_ads.options.purchase_label}`, value, currency: 'EUR', transaction_id: orderId });
    }

    // --- Meta (Facebook / Instagram)
    if (active.meta_pixel) {
      const name = { product_view: 'ViewContent', add_to_cart: 'AddToCart', checkout_start: 'InitiateCheckout', purchase: 'Purchase' }[eventType];
      const params = { currency: 'EUR', value: value || 0 };
      if (isProductEvent) { params.content_name = itemName; params.content_ids = itemId ? [itemId] : undefined; params.content_type = 'product'; }
      window.fbq('track', name, params, orderId ? { eventID: `order_${orderId}` } : undefined);
    }

    // --- TikTok
    if (active.tiktok) {
      const name = { product_view: 'ViewContent', add_to_cart: 'AddToCart', checkout_start: 'InitiateCheckout', purchase: 'CompletePayment' }[eventType];
      window.ttq.track(name, { currency: 'EUR', value: value || 0, content_id: itemId, content_name: itemName, content_type: 'product' });
    }

    // --- Pinterest (« checkout » = commande payée chez Pinterest)
    if (active.pinterest) {
      if (eventType === 'product_view') window.pintrk('track', 'pagevisit', { product_id: itemId, product_name: itemName });
      if (eventType === 'add_to_cart') window.pintrk('track', 'addtocart', { value, currency: 'EUR', order_quantity: 1 });
      if (eventType === 'purchase') window.pintrk('track', 'checkout', { value, currency: 'EUR', order_id: orderId });
    }

    // --- PostHog
    if (active.posthog) {
      window.posthog.capture(eventType, { value, currency: 'EUR', product_id: itemId, product_name: itemName, order_id: orderId });
    }

    // --- Microsoft : Bing Ads + Clarity
    if (active.bing_uet) {
      window.uetq.push('event', gaName, eventType === 'purchase' ? { revenue_value: value, currency: 'EUR' } : {});
    }
    if (active.clarity) window.clarity('event', eventType);
  } catch (e) {
    // Un traceur en panne ou bloqué ne doit jamais gêner le site ni le paiement.
  }
}

/* ------------------------------------- paramètres de campagne sur page privée ---- */

// Le lien « Personnaliser votre mini-site » de l'e-mail de commande arrive sur une page qui n'est
// volontairement pas suivie. On met de côté ses paramètres utm_* : TrackingProvider les attache à
// la première page publique visitée ensuite dans le même onglet (jamais l'adresse privée elle-même).
const PENDING_UTM_KEY = 'jm_pending_utm';
const UTM_NAMES = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];

export const rememberUtmFromPrivatePage = (search) => {
  try {
    const params = new URLSearchParams(search);
    if (!params.get('utm_source')) return;
    const utm = {};
    UTM_NAMES.forEach((name) => { utm[name] = (params.get(name) || '').slice(0, 100); });
    window.sessionStorage.setItem(PENDING_UTM_KEY, JSON.stringify(utm));
  } catch (e) { /* rien */ }
};

export const takePendingUtm = () => {
  try {
    const raw = window.sessionStorage.getItem(PENDING_UTM_KEY);
    if (!raw) return null;
    window.sessionStorage.removeItem(PENDING_UTM_KEY);
    const utm = JSON.parse(raw);
    return utm && utm.utm_source ? utm : null;
  } catch (e) {
    return null;
  }
};
